import Splide from '@splidejs/splide';
import { Intersection } from '@splidejs/splide-extension-intersection';

/**
 * スライダー
 *
 * .splide クラスを持つ要素に対してスライダーを初期化します
 *
 * 使用例:
 * document.addEventListener('DOMContentLoaded', () => {
 *   new Slider(); // デフォルトセレクターを使用
 *   // または
 *   new Slider('.custom-slider'); // カスタムセレクターを指定
 * });
 *
 * 追加機能01:
 * .splide__trackWrapper で .splide__track をラップしている場合、
 * .splide__trackWrapperの左側からbodyの右端（スクロールバー）までの距離を取得して、
 * その幅を.splide__trackWrapperの幅にします。
 * リサイズが発生した場合、再計算する。
 *
 * 追加機能02:
 * is-splide--spDestoy クラスを持つ場合、スライダーを破棄します。
 * ブレークポイントは 960px です。
 *
 * 追加機能03:
 * tabindex="-1" の変更を監視し、必要に応じて下層要素の tabindex を管理します。
 */
class Slider {
  private readonly selector: string;
  private splideInstances: Map<Element, Splide> = new Map();
  private resizeListeners: Map<Element, () => void> = new Map();
  private readonly breakpoint: number = 960;

  /**
   * コンストラクター
   * @param elementSelector - スライダー要素のセレクター
   */
  constructor(elementSelector: string = '.splide') {
    this.selector = elementSelector;
    this.init();
  }

  /**
   * 初期化
   */
  private init(): void {
    const elements = document.querySelectorAll(this.selector);
    if (!elements.length) {
      console.warn('スライダー要素が見つかりません');
      return;
    }

    elements.forEach((element) => {
      // idを取得
      let id = element.id;
      if (!id) {
        // idがない場合はidを生成
        id = `ist-splide-${Math.random().toString(36).substring(2, 15)}`;
        // idをセット
        element.id = id;
      }

      // 追加機能02: is-splide--spDestoy クラスを持つか確認
      const shouldDestroyOnBreakpoint = element.classList.contains(
        'is-splide--spDestoy'
      );

      if (shouldDestroyOnBreakpoint) {
        // 初期化するかどうかを判断
        if (window.innerWidth >= this.breakpoint) {
          this.initializeSplide(element, id);
        }

        // リサイズ時の処理を設定
        const onResize = this.handleResize.bind(this, element, id);
        window.addEventListener('resize', onResize);
        this.resizeListeners.set(element, onResize);
      } else {
        // 追加機能02が不要な場合は通常通り初期化
        this.initializeSplide(element, id);
      }

      /**
       * 追加機能01: .splide__trackWrapper の幅を調整
       */
      const trackWrapper = element.querySelector(
        '.splide__trackWrapper'
      ) as HTMLElement | null;
      if (trackWrapper) {
        const adjustTrackWrapperWidth = () => {
          // bodyのクライアント幅を取得
          const bodyWidth = document.body.clientWidth;

          // .splide__trackWrapperの左端の位置を取得（ページ全体の左からの距離）
          const trackRect = trackWrapper.getBoundingClientRect();
          const trackLeft = trackRect.left + window.scrollX;

          // bodyの右端までの距離を計算
          const distanceToRight = bodyWidth - trackLeft;

          // 計算した距離を.splide__trackWrapperの幅として設定
          trackWrapper.style.width = `${distanceToRight}px`;
        };

        /**
         * デバウンス関数
         * @param func - 実行する関数
         * @param wait - 待機時間（ミリ秒）
         * @returns デバウンスされた関数
         */
        const debounce = (func: Function, wait: number) => {
          let timeout: number | undefined;
          return () => {
            clearTimeout(timeout);
            timeout = window.setTimeout(() => {
              func();
            }, wait);
          };
        };

        // デバウンスされた関数を作成
        const debouncedAdjustTrackWrapperWidth = debounce(
          adjustTrackWrapperWidth,
          100
        );

        // 初回調整
        adjustTrackWrapperWidth();

        // リサイズ時にデバウンスされた関数を呼び出す
        window.addEventListener('resize', debouncedAdjustTrackWrapperWidth);

        // クリーンアップ用に、スライダーが破棄された際にイベントリスナーを解除
        if (shouldDestroyOnBreakpoint) {
          const splide = this.splideInstances.get(element);
          if (splide) {
            splide.on('destroy', () => {
              window.removeEventListener(
                'resize',
                debouncedAdjustTrackWrapperWidth
              );
            });
          }
        }
      }
    });
  }

  /**
   * Splideを初期化し、関連機能をセットアップします
   * @param element - スライダー要素
   * @param id - 要素のID
   */
  private initializeSplide(element: Element, id: string): void {
    if (this.splideInstances.has(element)) {
      // 既に初期化されている場合はスキップ
      return;
    }

    // スライダーを初期化
    const slider = new Splide(`#${id}`, {
      // 必要に応じてオプションを追加
    });

    // マウント前にイベントをバインド
    slider.on('ready', () => {
      // マウント完了時に初期状態をチェック
      //console.debug('ready');
      // 初期状態の設定
      slider.Components.Elements.slides.forEach((slide) => {
        const slideElement = slide as HTMLElement;
        //console.debug('slideElement', slideElement);
        if (slideElement.getAttribute('aria-hidden') === 'true') {
          this.setTabindexForSlide(slideElement, '-1');
        } else {
          this.restoreTabindexForSlide(slideElement);
        }
      });
    });

    // 追加機能03: tabindexの管理
    this.setupTabindexManagement(slider);

    // スライダーをマウント
    slider.mount({ Intersection });
    this.splideInstances.set(element, slider);

    /**
     * 自動再生機能付きの場合
     */
    // .slide-current / .slide-length を取得
    const slideCurrent = element.querySelector('.slide-current');
    const slideLength = element.querySelector('.slide-length');

    // もし、.slide-current / .slide-length があれば、それぞれの要素にテキストをセット
    if (slideCurrent && slideLength) {
      // 初期値を設定
      this.updateSlideInfo(slider, slideCurrent, slideLength);

      // スライド切り替え時のイベントリスナーを設定
      slider.on('moved', () => {
        this.updateSlideInfo(slider, slideCurrent, slideLength);
      });

      slider.on('active', () => {
        this.updateSlideInfo(slider, slideCurrent, slideLength);
      });
    }

    /**
     * 自動再生機能なしの場合
     */
    // .ist-carouselProgress / .ist-carouselProgress__bar を取得
    const carouselProgress = element.querySelector('.ist-carouselProgress');
    const carouselProgressBar = element.querySelector<HTMLElement>(
      '.ist-carouselProgress__bar'
    );

    // もし、.ist-carouselProgress / .ist-carouselProgress__bar があれば、スライダーの現在地表示
    if (carouselProgress && carouselProgressBar) {
      // 表示領域に完全に収まるスライドの数を計算
      const calculateVisibleSlides = () => {
        // 表示領域（トラック）の取得
        const track = element.querySelector('.splide__track');
        if (!track) {
          console.warn('Splideのトラックが見つかりません');
          return 0;
        }
        const trackWidth = track.clientWidth;

        // 1枚目のスライドを取得（全スライドの幅は同じと仮定）
        const slide = track.querySelector('.splide__slide');
        if (!slide) {
          console.warn('スライド要素が見つかりません');
          return 0;
        }

        // 1枚のスライドの幅と左右のマージンを取得
        const slideStyle = window.getComputedStyle(slide);
        const slideWidth = slide.clientWidth;
        const marginLeft = parseFloat(slideStyle.marginLeft) || 0;
        const marginRight = parseFloat(slideStyle.marginRight) || 0;

        // 1枚分のトータル幅
        const totalSlideWidth = slideWidth + marginLeft + marginRight;

        // 表示領域に完全に収まるスライドの数を計算
        const visibleSlides = Math.floor(trackWidth / totalSlideWidth);

        return visibleSlides;
      };

      // バーの長さを更新する関数
      const progressAction = () => {
        const visibleSlides = calculateVisibleSlides();
        const end = slider.Components.Controller.getEnd() + 1;
        const currentIndex =
          visibleSlides > 1
            ? slider.index + 1 + (visibleSlides - 1)
            : slider.index + 1;
        const rate = Math.min(currentIndex / end, 1);
        carouselProgressBar.style.width = `${100 * rate}%`;
      };

      // Splideの初期化が終わった際、またはスライドが切り替わる際にバーの長さを更新する
      slider.on('mounted move', progressAction);

      // バーの長さを更新
      progressAction();

      // 自動再生機能なしの場合 move イベントをバインド
      const nextButton = element.querySelector('.splide__arrow--next');
      const prevButton = element.querySelector('.splide__arrow--prev');
      const slideLength = slider.Components.Slides.getLength();
      if (nextButton && prevButton) {
        // moveイベントを削除
        //slider.off('move');

        // nextButtonのクリックイベントをバインド
        nextButton.addEventListener('click', () => {
          const currentIndex = slider.index;
          const visibleSlides = calculateVisibleSlides();

          if (slideLength - (visibleSlides - 1) === currentIndex) {
            slider.go(0);
          } else {
            slider.go(currentIndex);
          }
        });

        // prevButtonのクリックイベントをバインド
        prevButton.addEventListener('click', () => {
          const currentIndex = slider.index;
          const visibleSlides = calculateVisibleSlides();

          if (slideLength - 1 === currentIndex && visibleSlides > 1) {
            slider.go(currentIndex - (visibleSlides - 1));
          } else {
            slider.go(currentIndex);
          }
        });
      }
    }

    /**
     * 追加機能01: .splide__trackWrapper の幅を調整
     */
    const trackWrapper = element.querySelector(
      '.splide__trackWrapper'
    ) as HTMLElement | null;
    if (trackWrapper) {
      const adjustTrackWrapperWidth = () => {
        // bodyのクライアント幅を取得
        const bodyWidth = document.body.clientWidth;

        // .splide__trackWrapperの左端の位置を取得（ページ全体の左からの距離）
        const trackRect = trackWrapper.getBoundingClientRect();
        const trackLeft = trackRect.left + window.scrollX;

        // bodyの右端までの距離を計算
        const distanceToRight = bodyWidth - trackLeft;

        // 計算した距離を.splide__trackWrapperの幅として設定
        trackWrapper.style.width = `${distanceToRight}px`;
      };

      /**
       * デバウンス関数
       * @param func - 実行する関数
       * @param wait - 待機時間（ミリ秒）
       * @returns デバウンスされた関数
       */
      const debounce = (func: Function, wait: number) => {
        let timeout: number | undefined;
        return () => {
          clearTimeout(timeout);
          timeout = window.setTimeout(() => {
            func();
          }, wait);
        };
      };

      // デバウンスされた関数を作成
      const debouncedAdjustTrackWrapperWidth = debounce(
        adjustTrackWrapperWidth,
        100
      );

      // 初回調整
      adjustTrackWrapperWidth();

      // リサイズ時にデバウンスされた関数を呼び出す
      window.addEventListener('resize', debouncedAdjustTrackWrapperWidth);

      // クリーンアップ用に、スライダーが破棄された際にイベントリスナーを解除
      const onDestroy = () => {
        window.removeEventListener('resize', debouncedAdjustTrackWrapperWidth);
      };
      slider.on('destroy', onDestroy);
    }
  }

  /**
   * スライドの可視性変更時にtabindexを管理します
   * @param slider - Splideインスタンス
   */
  private setupTabindexManagement(slider: Splide): void {
    // visibleイベント: スライドが表示される時
    slider.on('visible', (slideComponent) => {
      this.restoreTabindexForSlide(slideComponent.slide as HTMLElement);
    });

    // hiddenイベント: スライドが非表示になる時
    slider.on('hidden', (slideComponent) => {
      this.setTabindexForSlide(slideComponent.slide as HTMLElement, '-1');
    });
  }

  /**
   * スライドの子要素のtabindexをリセット
   * @param slideElement - スライド要素
   */
  private restoreTabindexForSlide(slideElement: HTMLElement): void {
    const childElements = slideElement.querySelectorAll('[tabindex]');
    childElements.forEach((child) => {
      const originalTabindex = child.getAttribute('data-original-tabindex');
      if (originalTabindex !== null) {
        child.setAttribute('tabindex', originalTabindex);
        child.removeAttribute('data-original-tabindex');
      }
    });
  }

  /**
   * スライドの子要素のtabindexを設定
   * @param slideElement - スライド要素
   * @param tabindex - tabindexの値
   */
  private setTabindexForSlide(
    slideElement: HTMLElement,
    tabindex: string
  ): void {
    const childElements = slideElement.querySelectorAll('[tabindex]');
    childElements.forEach((child) => {
      if (!child.hasAttribute('data-original-tabindex')) {
        const originalTabindex = child.getAttribute('tabindex');
        if (originalTabindex !== null) {
          child.setAttribute('data-original-tabindex', originalTabindex);
        }
      }
      child.setAttribute('tabindex', tabindex);
    });
  }

  /**
   * ウィンドウのリサイズ時にSplideを初期化または破棄します
   * @param element - スライダー要素
   * @param id - 要素のID
   */
  private handleResize(element: Element, id: string): void {
    const slider = this.splideInstances.get(element);
    const shouldInitialize = window.innerWidth >= this.breakpoint;
    const hasSlider = !!slider;

    if (shouldInitialize && !hasSlider) {
      // 初期化
      this.initializeSplide(element, id);
    } else if (!shouldInitialize && hasSlider) {
      // 破棄
      slider?.destroy();
      this.splideInstances.delete(element);
    }
  }

  /**
   * スライド情報を更新
   */
  private updateSlideInfo(
    slider: Splide,
    currentElement: Element,
    lengthElement: Element
  ): void {
    // 現在のインデックスを計算（ループ考慮）
    const currentIndex = (slider.index % slider.length) + 1;
    currentElement.textContent = String(currentIndex);

    // 実際のスライド数を取得（ループによる重複を除外）
    lengthElement.textContent = String(slider.length);
  }
}

export default Slider;
